import { FilterOperator, useSearchExamCenterProctorsQuery } from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import { type GridEventListener, GroupOutlinedIcon, NoRowsOverlay } from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';
import { useCallback, useMemo } from 'react';

import { ExamCenterProctorDataGrid } from '../../components';
import { type ExamCenterProctorListItem } from '../../components/exam-center-proctor-data-grid/columns';
import { useExamCenterProctorListSearchParams } from '../../hooks';

interface ExamCenterProctorListProps {
  examCenterCode: string;
}

export default function ExamCenterProctorList(props: Readonly<ExamCenterProctorListProps>) {
  const { examCenterCode } = props;
  const navigate = useNavigate();
  const [
    { page, pageSize, sortOrder, search, proctorTypes },
    { changePageNumber, changePageSize, changeSortingOrder },
  ] = useExamCenterProctorListSearchParams();

  const { data: searchExamCenterProctorsResult, loading: isSearching } =
    useSearchExamCenterProctorsQuery({
      variables: {
        searchExamCenterProctorsInput: {
          filter: {
            searchText: {
              fields: ['fullName'],
              value: search,
            },
            conditions: [
              {
                field: 'examCenterCode',
                value: examCenterCode,
                operator: FilterOperator.Equal,
              },
              {
                field: 'type',
                value: proctorTypes.length > 0 ? proctorTypes : null,
                operator: FilterOperator.In,
              },
            ].filter((condition) => condition.value !== null),
          },
          order: {
            field: 'createdAt',
            type: ORDER_TYPE[sortOrder],
          },
          paging: {
            pageSize: pageSize,
            currentPage: page,
          },
        },
      },
    });

  const { examCenterProctorItems, total } = useMemo(() => {
    const searchResults = searchExamCenterProctorsResult?.ecSearchExamCenterProctors;

    if (!searchResults) {
      return { examCenterProctorItems: [], total: 0 };
    }

    const examCenterProctorItems = searchResults.examCenterProctors.map(
      (proctor): ExamCenterProctorListItem => ({
        id: proctor.id,
        type: proctor.type,
        fullName: [proctor.titleTh, proctor.firstNameTh, proctor.middleNameTh, proctor.lastNameTh]
          .filter(Boolean)
          .join(' '),
        approvalDate: proctor.createdAt,
      }),
    );

    return {
      examCenterProctorItems,
      total: searchResults.total,
    };
  }, [searchExamCenterProctorsResult?.ecSearchExamCenterProctors]);

  const handleRowClick: GridEventListener<'rowClick'> = useCallback(
    (params) => {
      navigate('/:examCenterCode/exam-proctors/proctors/:proctorId', {
        params: {
          proctorId: params.row.id,
          examCenterCode,
        },
      });
    },
    [navigate, examCenterCode],
  );

  const isUserSearching = useMemo(() => {
    const isSearchingText = search.length > 0;
    const isFilteringByProctorTypes = proctorTypes.length > 0;

    return isSearchingText || isFilteringByProctorTypes;
  }, [search, proctorTypes]);

  return (
    <ExamCenterProctorDataGrid
      sortingMode='server'
      rows={examCenterProctorItems}
      loading={isSearching}
      onRowClick={handleRowClick}
      slots={{
        noRowsOverlay: () => {
          if (isUserSearching) {
            return <NoRowsOverlay />;
          }

          return <NoRowsOverlay title='ยังไม่มีรายชื่อผู้คุมสอบ' icon={GroupOutlinedIcon} />;
        },
      }}
      initialState={{
        sorting: {
          sortModel: [{ field: 'approvalDate', sort: sortOrder }],
        },
      }}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];
        if (sortModel?.sort) {
          return changeSortingOrder(sortModel.sort);
        }

        return changeSortingOrder('desc');
      }}
      slotProps={{
        footer: {
          total: total,
          pagination: {
            page: page,
            count: total,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              const pageSize = parseInt(event.target.value);
              return changePageSize(pageSize);
            },
          },
        },
      }}
    />
  );
}
